import { useCallback, useRef, useState } from "react";
import { axiosInstance } from "@/contexts/axiosApiInstance";

// QUDT descriptions are static text. 8s mirrors useUnitSearch — same
// "something is fundamentally wrong" boundary.
const TIMEOUT_MS = 8000;

interface DetailsResponse {
  description: string | null;
}

export type DescriptionEntry =
  | { state: "loading" }
  | { state: "loaded"; text: string | null }
  | { state: "error" };

interface UseUnitDescription {
  /**
   * Synchronously read the current entry for a URI. `undefined` means no
   * fetch has been triggered yet — the caller should pass `null` as the
   * tooltip title so MUI suppresses the popup.
   */
  get: (uri: string) => DescriptionEntry | undefined;
  /**
   * Idempotent: safe to call on every tooltip open. Skips when an entry
   * already exists (loading, loaded, or error). The 1.5 s tooltip-enter
   * delay is the natural rate-limit upstream — most rows the user scrolls
   * past never trigger this.
   */
  fetch: (uri: string) => void;
}

/**
 * Per-picker cache of unit descriptions, populated lazily on hover. Cache
 * lives for the lifetime of the picker (consistent with the LRU in
 * `useUnitSearch`); no cross-picker sharing — descriptions are tiny so the
 * trade-off is fine.
 */
export function useUnitDescription(): UseUnitDescription {
  // useRef holds the canonical Map (survives renders); a numeric tick in
  // state forces re-render when an entry transitions.
  const cacheRef = useRef<Map<string, DescriptionEntry>>(new Map());
  const [, setTick] = useState(0);

  const get = useCallback((uri: string) => cacheRef.current.get(uri), []);

  const fetch = useCallback((uri: string) => {
    if (cacheRef.current.has(uri)) return;
    cacheRef.current.set(uri, { state: "loading" });
    setTick((n) => n + 1);

    axiosInstance
      .get<DetailsResponse>("/api/units/details", {
        params: { uri },
        timeout: TIMEOUT_MS,
      })
      .then((res) => {
        cacheRef.current.set(uri, {
          state: "loaded",
          text: res.data.description,
        });
        setTick((n) => n + 1);
      })
      .catch(() => {
        cacheRef.current.set(uri, { state: "error" });
        setTick((n) => n + 1);
      });
  }, []);

  return { get, fetch };
}
